import {
  Accordion,
  AccordionItemProps,
  Icon,
  useAccordionItemContext,
} from '@chakra-ui/react';
import { useId } from 'react';
import { MdChevronRight, MdExpandMore } from 'react-icons/md';
import { MAccordionItem, MAccordionPanel, MFlex, MText } from '../Monetize';

interface QuoteBodyAccordionProps extends Omit<
  AccordionItemProps,
  'value' | 'children'
> {
  value?: string;
  title: string;
  /**
   * Hides the expand icon on right side of accordion button
   */
  hideExpandIcon?: boolean;
  /**
   * Add detail content on right side of accordion button
   */
  rightContent?: React.ReactNode;
  /**
   * Hides the panel content
   */
  hidePanel?: boolean;
  children?:
    | React.ReactNode
    | ((props: { isExpanded: boolean }) => React.ReactNode);
}

interface QuoteBodyAccordionBodyProps {
  title: string;
  hideExpandIcon?: boolean;
  hidePanel?: boolean;
  children?: QuoteBodyAccordionProps['children'];
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own
// child component reading `useAccordionItemContext()`. This wrapper's own
// external API (function-or-plain children) is unchanged - 2 real callers
// (QuoteInternalBilling.tsx, OneTimeInvoiceTermsAndMemo.tsx) rely on it.
const QuoteBodyAccordionBody = ({
  title,
  hideExpandIcon,
  hidePanel,
  children,
}: QuoteBodyAccordionBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <h2>
        <Accordion.ItemTrigger px={2} onClick={() => {}} minH="unset">
          <MFlex alignItems="center" alignContent="spread" w="full" py={2}>
            <MFlex alignItems="center">
              {!hideExpandIcon && (
                <MFlex alignItems="center">
                  {isExpanded ? (
                    <Icon boxSize="1.5rem" asChild>
                      <MdExpandMore />
                    </Icon>
                  ) : (
                    <Icon boxSize="1.5rem" asChild>
                      <MdChevronRight />
                    </Icon>
                  )}
                </MFlex>
              )}
            </MFlex>
            <MFlex textAlign="left" alignItems="center" py={1} flexGrow={1}>
              <MText fontWeight="500" fontSize="lg" ml={2}>
                {title}
              </MText>
            </MFlex>
            {/* TODO: add back in the future if we need it */}
            {/* {rightContent} */}
          </MFlex>
        </Accordion.ItemTrigger>
      </h2>
      {!hidePanel && (
        <MAccordionPanel p="4">
          {typeof children === 'function' ? children({ isExpanded }) : children}
        </MAccordionPanel>
      )}
    </>
  );
};

export const QuoteBodyAccordion = ({
  title,
  children,
  hideExpandIcon,
  rightContent: _rightContent,
  hidePanel,
  value,
  ...rest
}: QuoteBodyAccordionProps) => {
  // v3's Accordion.Item requires a `value` (no implicit index-based
  // identity like v2) - none of this wrapper's real callers render more
  // than one standalone item and never had one to give, so fall back to a
  // stable generated id rather than requiring every caller to invent one.
  const generatedValue = useId();

  return (
    <MAccordionItem border="none" {...rest} value={value ?? generatedValue}>
      <QuoteBodyAccordionBody
        title={title}
        hideExpandIcon={hideExpandIcon}
        hidePanel={hidePanel}
      >
        {children}
      </QuoteBodyAccordionBody>
    </MAccordionItem>
  );
};
